import { AitRowGroupData } from "./AitRowGroupData";
import { newRowGroup } from "./newRowGroup";

/**
 * Add a new row group to the body data, optionally using a template.
 *
 * When `bodyData.length` is less than `newLength`, a new `AitRowGroupData` is
 * created (from `groupTemplates` if `templateName` matches) and inserted at
 * index `rgi + 1`. Column widths and columnIds are initialised to match the
 * existing first row group.
 *
 * @template T - The cell payload type
 * @param bodyData - Current array of body row groups
 * @param groupTemplates - Available row-group templates to use when creating a new group
 * @param newLength - Desired minimum number of row groups after insertion
 * @param defaultCellWidth - Width to use for newly created cells
 * @param blankT - Blank payload value used to initialise cells
 * @param rgi - Index in `bodyData` after which the new group will be inserted
 * @param templateName - Optional template name to select from `groupTemplates`
 * @returns Updated `AitRowGroupData[]` with the new row group inserted, or `undefined` when no insertion was necessary
 */
export const trAddRowGroup = <T>(
  bodyData: AitRowGroupData<T>[],
  groupTemplates: AitRowGroupData<T>[],
  newLength: number,
  defaultCellWidth: number,
  blankT: T,
  rgi: number,
  templateName?: string,
): AitRowGroupData<T>[] | undefined => {
  /* istanbul ignore else */
  if (bodyData.length < newLength) {
    const newBody = [...bodyData];
    // Create new body, take template if it can be found
    const ix =
      !templateName || !groupTemplates
        ? -1
        : groupTemplates.findIndex((g) => g.name === templateName);
    const newRowGroupTemplate: AitRowGroupData<T> =
      ix > -1 && groupTemplates
        ? groupTemplates[ix]
        : {
            aitid: crypto.randomUUID(),
            rows: [
              { aitid: crypto.randomUUID(), cells: [], coreTrafficLight: "BlankNotEvaluated" },
            ],
            coreTrafficLight: "BlankNotEvaluated",
          };
    // Ensure new template meets requirements
    if (bodyData.length) {
      // Get columnIds from existing bodyData
      const columnIds = bodyData[0].rows[0].cells.map((c) => c.columnId);
      const newrg: AitRowGroupData<T> = newRowGroup(
        defaultCellWidth,
        blankT,
        bodyData[0].rows[0].cells.length,
        newRowGroupTemplate,
        columnIds,
      );
      // Set traffic light to indicate this is a newly added row group
      newrg.coreTrafficLight = "ChangedUnknown";
      // Set column widths (columnIds already set by newRowGroup)
      newrg.rows.forEach((r) =>
        r.cells.forEach((c, ci) => {
          c.colWidth = bodyData![0].rows[0].cells[ci].colWidth;
        }),
      );
      // Copy existing body and splice in new data
      newBody.splice(rgi + 1, 0, newrg);
    }
    return newBody;
  }
};
